import React from 'react';
import { Box, Button, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import DescriptionIcon from '@mui/icons-material/Description';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GeneralEvolutionModal from '../generalEvolutionModal';
import GeneralEvolutionActionsMenu from '../generalEvolutionActionsMenu';
import GeneralEvolutionRectificationHistoryModal from '../rectification/generalEvolutionRectificationHistoryModal';
import { useParams } from 'react-router-dom';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import { useGeneralEvolutionListScreen } from '../hooks/useGeneralEvolutionListScreen';

export default function GeneralEvolutionListWeb() {
  const { patientId } = useParams();

  const {
    evolutions,
    isLoading,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
    isEvolutionModalOpen,
    isHistoryModalOpen,
    anchorEl,
    selectedEvolution,
    evolutionToRectify,
    openAddModal,
    openActionsMenu,
    closeActionsMenu,
    openRectifyModal,
    openHistoryModal,
    closeEvolutionModal,
    closeHistoryModal,
    onEvolutionSaved,
  } = useGeneralEvolutionListScreen(patientId);

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Evoluções Gerais
          </Typography>
          <Button data-cy="add-general-evolution-btn" variant="outlined" onClick={openAddModal}>
            Adicionar Evolução
          </Button>
        </Box>

        {evolutions.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }}>
            Nenhuma evolução geral cadastrada ainda.
          </Typography>
        ) : (
          <Timeline sx={{ p: 0, [`& .MuiTimelineItem-root::before`]: { flex: 0, padding: 0 } }}>
            {evolutions.map((evo, index) => (
              <TimelineItem key={evo.id}>
                <TimelineSeparator>
                  <TimelineDot sx={{ backgroundColor: '#F5F5F5' }}>
                    <DescriptionIcon fontSize="small" color="primary" />
                  </TimelineDot>
                  {index < evolutions.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {evo.data}
                      </Typography>
                      {evo.rectified && (
                        <Tooltip title="Evolução retificada">
                          <Chip
                            label="Retificada"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {evo.userName?.trim() && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          Adicionada por: {evo.userName}
                        </Typography>
                      )}
                      <IconButton
                        data-cy="general-evolution-actions-btn"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={(event) => openActionsMenu(event, evo)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1, whiteSpace: 'pre-line' }}
                  >
                    {evo.evolucao}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </Paper>

      <GeneralEvolutionActionsMenu
        anchorEl={anchorEl}
        evolution={selectedEvolution}
        onClose={closeActionsMenu}
        onRectify={openRectifyModal}
        onViewRectifications={openHistoryModal}
      />

      <GeneralEvolutionModal
        open={isEvolutionModalOpen}
        onClose={closeEvolutionModal}
        onEvolutionSave={onEvolutionSaved}
        evolutionToRectify={evolutionToRectify}
      />

      <GeneralEvolutionRectificationHistoryModal
        open={isHistoryModalOpen}
        onClose={closeHistoryModal}
        evolution={selectedEvolution}
      />
    </>
  );
}
